import React, { useEffect, useState } from 'react';

/**
 * SafeHtml — CKEditor-ээс ирсэн HTML контентыг XSS-аас хамгаалж render-дэх компонент.
 *
 * DOMPurify нь зөвхөн client side-д ажиллана. Server side render үед raw HTML харагдах
 * боловч (rehydrate-ийн өмнө хариу үзэгдэх) Next.js нь HTML escape хийгээгүй markup-ийг
 * render-дэх тул аюулгүйн үүднээс sanitize хийгээд placeholder харуулна.
 *
 * Хэрэглээ:
 *   <SafeHtml html={item.body} className="prose-content" />
 */
export default function SafeHtml({ html, className = '', as: As = 'div' }) {
    const [sanitized, setSanitized] = useState('');
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!html) {
            setSanitized('');
            setReady(true);
            return;
        }

        // DOMPurify dynamic import — server-аас зайлуулах
        let cancelled = false;
        (async () => {
            try {
                const DOMPurify = (await import('dompurify')).default;
                if (cancelled) return;

                // Аюулгүй гэж зөвшөөрөгдсөн tag/attr
                const clean = DOMPurify.sanitize(html, {
                    ALLOWED_TAGS: [
                        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                        'p', 'br', 'hr',
                        'strong', 'em', 'b', 'i', 'u', 's', 'mark', 'sub', 'sup',
                        'ul', 'ol', 'li',
                        'blockquote', 'pre', 'code',
                        'a', 'img', 'figure', 'figcaption',
                        'table', 'thead', 'tbody', 'tr', 'th', 'td',
                        'span', 'div',
                        // Видео embed
                        'iframe',
                    ],
                    ALLOWED_ATTR: [
                        'href',
                        'target',
                        'rel',
                        'src',
                        'alt',
                        'title',
                        'class',
                        'style',
                        'width',
                        'height',
                        'allowfullscreen',
                        'frameborder',
                        'loading',
                        'colspan',
                        'rowspan',
                    ],
                    ALLOWED_URI_REGEXP:
                        /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
                    ADD_ATTR: ['target'],
                });

                // External link-үүдэд rel="noopener" нэмэх
                const tmp = document.createElement('div');
                tmp.innerHTML = clean;
                tmp.querySelectorAll('a[target="_blank"]').forEach((a) => {
                    const cur = a.getAttribute('rel') || '';
                    if (!cur.includes('noopener')) {
                        a.setAttribute(
                            'rel',
                            (cur + ' noopener noreferrer').trim()
                        );
                    }
                });

                // Зургийн lazy loading
                tmp.querySelectorAll('img').forEach((img) => {
                    if (!img.getAttribute('loading')) {
                        img.setAttribute('loading', 'lazy');
                    }
                });

                setSanitized(tmp.innerHTML);
            } catch (e) {
                console.warn('DOMPurify failed, falling back to plain text', e);
                // Fallback: HTML tag-уудыг устгаад текст төдийгөөр
                setSanitized(html.replace(/<[^>]+>/g, ''));
            } finally {
                setReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [html]);

    if (!ready) {
        // SSR-д server render үед html-ийг бүрэн нуух
        // (DOMPurify client дээр ачаалагдсаны дараа л харуулна)
        return <As className={className} />;
    }

    return (
        <As
            className={className}
            dangerouslySetInnerHTML={{ __html: sanitized }}
        />
    );
}
